import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './weatherDashboard.css';

export default function WeatherDashboard({ airport }) {
  const { t } = useTranslation();

  const weatherState = useMemo(() => {
    // Determine weather state in this priority:
    // Thunderstorm > Rain > Snow > Fog > Cloudy > Sunny
    const conditionCodes = (airport.conditions || []).map(c => c.code);
    const cloudCodes = (airport.clouds || []).map(c => c.code);
    
    const hasCode = (codes, target) => codes.some(code => code.includes(target));
    
    if (hasCode(conditionCodes, 'TS')) return 'thunderstorm';
    if (hasCode(conditionCodes, 'RA') || hasCode(conditionCodes, 'DZ') || hasCode(conditionCodes, 'SH')) return 'rain';
    if (hasCode(conditionCodes, 'SN') || hasCode(conditionCodes, 'SG')) return 'snow';
    if (hasCode(conditionCodes, 'FG') || hasCode(conditionCodes, 'BR') || hasCode(conditionCodes, 'HZ')) return 'fog';
    if (hasCode(cloudCodes, 'OVC') || hasCode(cloudCodes, 'BKN')) return 'cloudy';
    return 'sunny';
  }, [airport]);

  // Guard clause if no advanced data is available
  if (airport.temperature === null && airport.pressure === null) {
    return null;
  }

  const renderWeatherBackground = () => {
    switch (weatherState) {
      case 'rain':
        return (
          <div className="weather-bg-container">
            {Array.from({ length: 25 }).map((_, i) => (
              <div 
                key={i} 
                className="anim-rain-drop" 
                style={{ 
                  left: `${Math.random() * 100}%`, 
                  animationDelay: `${Math.random() * 0.8}s`,
                  opacity: Math.random() * 0.4 + 0.2
                }} 
              />
            ))}
          </div>
        );
      case 'snow':
        return (
          <div className="weather-bg-container">
            {Array.from({ length: 25 }).map((_, i) => (
              <div 
                key={i} 
                className="anim-snow-flake" 
                style={{ 
                  left: `${Math.random() * 100}%`, 
                  width: `${Math.random() * 4 + 2}px`,
                  height: `${Math.random() * 4 + 2}px`,
                  animationDelay: `${Math.random() * 4}s`,
                  opacity: Math.random() * 0.5 + 0.3
                }} 
              />
            ))}
          </div>
        );
      case 'fog':
        return (
          <div className="weather-bg-container">
            <div className="anim-fog" />
          </div>
        );
      case 'thunderstorm':
        return (
          <div className="weather-bg-container">
            {Array.from({ length: 25 }).map((_, i) => (
              <div 
                key={i} 
                className="anim-rain-drop" 
                style={{ 
                  left: `${Math.random() * 100}%`, 
                  animationDelay: `${Math.random() * 0.8}s`,
                  opacity: Math.random() * 0.4 + 0.2
                }} 
              />
            ))}
            <div className="anim-lightning" />
          </div>
        );
      case 'cloudy':
        return (
          <div className="weather-bg-container">
            <div className="anim-clouds-bg" />
          </div>
        );
      case 'sunny':
      default:
        return (
          <div className="weather-bg-container">
            <div className="anim-sun" />
          </div>
        );
    }
  };

  const renderConditions = () => {
    if (!airport.conditions || airport.conditions.length === 0) return null;
    return (
      <span className="text-[10px] text-aviation-cyan mt-1 uppercase tracking-wider text-center px-2">
        {airport.conditions.map(c => c.text).join(", ")}
      </span>
    );
  };

  return (
    <section className="mt-8 mb-16 px-2 sm:px-0 relative" aria-labelledby="weather-dashboard-heading">
      {renderWeatherBackground()}
      
      <h2 id="weather-dashboard-heading" className="text-[10px] sm:text-xs font-bold text-aviation-cyan tracking-widest uppercase mb-4 text-center">
        {t("airport.weatherIntelligence") || "Live Weather Intelligence"}
      </h2>
      
      {/* Flight Category Badge */}
      <div className="flex justify-center mb-6">
        <div className={`px-3 sm:px-4 py-1 border border-slate-700 bg-slate-800/80 backdrop-blur-md rounded-full flex items-center gap-2`}>
          <div className={`w-2 h-2 rounded-full animate-pulse bg-current flight-cat-${airport.flight_category}`}></div>
          <span className={`text-xs sm:text-sm font-bold tracking-widest flight-cat-${airport.flight_category}`}>
            {airport.flight_category}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 max-w-4xl mx-auto w-full">
        
        {/* Temperature Card */}
        <div className="weather-dashboard-card relative overflow-hidden glass-card rounded-card p-3 sm:p-5 shadow-card border border-slate-800 bg-slate-900/50 backdrop-blur-md flex flex-col items-center justify-center min-h-[100px] sm:min-h-[120px]">
          <span className="text-[10px] sm:text-xs text-slate-400 mb-1 sm:mb-2">{t("airport.temperature") || "Temperature"}</span>
          <div className="relative">
            <span className="text-2xl sm:text-3xl font-mono font-bold text-white relative z-10">
              {airport.temperature !== null ? `${airport.temperature}°C` : "--"}
            </span>
            {airport.temperature !== null && (
              <div className="absolute inset-0 bg-orange-500/10 blur-xl rounded-full anim-heat-shimmer z-0"></div>
            )}
          </div>
        </div>

        {/* Visibility Card */}
        <div className="weather-dashboard-card relative overflow-hidden glass-card rounded-card p-3 sm:p-5 shadow-card border border-slate-800 bg-slate-900/50 backdrop-blur-md flex flex-col items-center justify-center min-h-[100px] sm:min-h-[120px]">
          <span className="text-[10px] sm:text-xs text-slate-400 mb-1 sm:mb-2">{t("airport.visibility") || "Visibility"}</span>
          <span className="text-xl sm:text-2xl font-mono font-bold text-white anim-visibility-glow text-center z-10">
            {airport.visibility !== null ? `${(airport.visibility / 1000).toFixed(1)} km` : "--"}
          </span>
          {renderConditions()}
        </div>

        {/* Humidity Card */}
        <div className="weather-dashboard-card relative overflow-hidden glass-card rounded-card p-3 sm:p-5 shadow-card border border-slate-800 bg-slate-900/50 backdrop-blur-md flex flex-col items-center justify-center min-h-[100px] sm:min-h-[120px]">
          <span className="text-[10px] sm:text-xs text-slate-400 mb-1 sm:mb-2">{t("airport.humidity") || "Humidity"}</span>
          <span className="text-2xl sm:text-3xl font-mono font-bold text-white relative z-10">
            {airport.humidity !== null ? `${airport.humidity}%` : "--"}
          </span>
          {airport.humidity !== null && (
            <div className="absolute inset-x-0 bottom-0 h-10 overflow-hidden pointer-events-none z-0">
               <div className="anim-humidity-particle"></div>
               <div className="anim-humidity-particle"></div>
               <div className="anim-humidity-particle"></div>
            </div>
          )}
        </div>

        {/* Pressure Card */}
        <div className="weather-dashboard-card relative overflow-hidden glass-card rounded-card p-3 sm:p-5 shadow-card border border-slate-800 bg-slate-900/50 backdrop-blur-md flex flex-col items-center justify-center min-h-[100px] sm:min-h-[120px]">
          <span className="text-[10px] sm:text-xs text-slate-400 mb-1 sm:mb-2">{t("airport.pressure") || "Pressure"}</span>
          <div className="relative flex flex-col items-center">
            <span className="text-xl sm:text-2xl font-mono font-bold text-white relative z-10">
              {airport.pressure !== null ? `${airport.pressure}` : "--"}
            </span>
            <span className="text-[10px] text-slate-500 font-mono mt-1 z-10">hPa</span>
            {airport.pressure !== null && (
              <div className="absolute inset-0 bg-aviation-cyan/5 border border-aviation-cyan/20 rounded-full anim-pressure-pulse z-0 -m-4"></div>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}
